/**
 * Human-facing formatting for the store and fleet lists.
 *
 * Every function that depends on the clock takes it as an argument rather than
 * reading it, so two rows rendered in the same paint cannot disagree about what
 * "now" is, and so the wording is testable without freezing time globally.
 *
 * Sizes use 1024-based divisors with the familiar KB/MB/GB labels, because the
 * numbers being formatted come from Android's StatFs, which counts in binary.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

function plural(count: number, singular: string): string {
  return count === 1 ? `1 ${singular}` : `${count} ${singular}s`;
}

/**
 * "4 minutes ago" and friends. A timestamp slightly in the future reads as "just
 * now" rather than as nonsense: server and browser clocks disagree by a second
 * or two all the time, and that is not worth a scary label.
 */
export function formatRelativeTime(iso: string | null, nowMs: number): string {
  if (iso === null) return 'Never';

  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'Unknown';

  const elapsed = Math.max(0, nowMs - then);
  if (elapsed < 45_000) return 'just now';
  if (elapsed < HOUR_MS) return `${plural(Math.round(elapsed / MINUTE_MS), 'minute')} ago`;
  if (elapsed < DAY_MS) return `${plural(Math.floor(elapsed / HOUR_MS), 'hour')} ago`;
  if (elapsed < 7 * DAY_MS) return `${plural(Math.floor(elapsed / DAY_MS), 'day')} ago`;
  return `${plural(Math.floor(elapsed / (7 * DAY_MS)), 'week')} ago`;
}

/** The full timestamp, for the `title` of whatever shows the relative one. */
export function formatAbsoluteTime(iso: string | null): string {
  if (iso === null) return 'No heartbeat recorded';
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return 'Unknown';
  return new Date(parsed).toLocaleString();
}

const BYTE_UNITS = ['bytes', 'KB', 'MB', 'GB', 'TB'] as const;

export function formatBytes(bytes: number | null): string {
  if (bytes === null || !Number.isFinite(bytes)) return 'Unknown';
  if (bytes < 1024) return `${Math.round(bytes)} bytes`;

  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < BYTE_UNITS.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const unit = BYTE_UNITS[unitIndex] ?? 'bytes';
  const decimals = value >= 100 ? 0 : 1;
  return `${value.toFixed(decimals)} ${unit}`;
}

/** Minutes as "15h 30m". Used for how long a store is open on a given day. */
export function formatMinutes(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes));
  const hours = Math.floor(whole / 60);
  const rest = whole % 60;
  if (hours === 0) return `${rest}m`;
  if (rest === 0) return `${hours}h`;
  return `${hours}h ${rest}m`;
}

/**
 * How far a device's clock has drifted from the server's. A device more than a
 * minute out will cross a daypart boundary at the wrong moment, which is why
 * this is on the dashboard at all.
 */
export function formatClockSkew(skewMs: number | null): string {
  if (skewMs === null || !Number.isFinite(skewMs)) return 'Unknown';

  const magnitude = Math.abs(skewMs);
  if (magnitude < 1000) return 'In step';

  const sign = skewMs > 0 ? 'ahead' : 'behind';
  if (magnitude < MINUTE_MS) return `${Math.round(magnitude / 1000)}s ${sign}`;
  return `${formatMinutes(magnitude / MINUTE_MS)} ${sign}`;
}

export function formatScreenSize(sizeInches: number | null): string {
  if (sizeInches === null || !Number.isFinite(sizeInches)) return 'Not recorded';
  const rounded = Math.round(sizeInches * 10) / 10;
  return `${rounded}"`;
}

export function formatAppVersion(versionCode: number | null): string {
  return versionCode === null ? 'Unknown' : `Build ${versionCode}`;
}
